import { useState, useEffect } from 'react';
import { usePomodoro, PHASE, PHASE_LABELS } from '../contexts/PomodoroContext';

const C = {
  [PHASE.FOCUS]: 'var(--accent)',
  [PHASE.SHORT]: '#16A34A',
  [PHASE.LONG]:  '#7C3AED',
};
const EMOJI = {
  [PHASE.FOCUS]: '🎯',
  [PHASE.SHORT]: '☕',
  [PHASE.LONG]:  '🌿',
};

// ── Thin SVG arc ──────────────────────────────────────────────────────────
function Arc({ pct, size, color, running }) {
  const r = (size / 2) - 12;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--border)" strokeWidth="4"/>
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke={color} strokeWidth="4" strokeLinecap="round"
        strokeDasharray={`${circ * pct / 100} ${circ}`}
        style={{ transition: running ? 'stroke-dasharray 1s linear' : 'none' }}
      />
    </svg>
  );
}

// ── Settings view ─────────────────────────────────────────────────────────
function Settings() {
  const { durations, sound, updateDuration, setSound } = usePomodoro();
  const rows = [
    { p: PHASE.FOCUS, label: 'Focus',      emoji: '🎯' },
    { p: PHASE.SHORT, label: 'Short break', emoji: '☕' },
    { p: PHASE.LONG,  label: 'Long break',  emoji: '🌿' },
  ];
  return (
    <div style={{ padding: '0 18px 20px' }}>
      <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>
        Timer durations
      </p>
      {rows.map(({ p, label, emoji }) => (
        <div key={p} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, padding: '9px 12px', background: 'var(--bg)', borderRadius: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ fontSize: 16 }}>{emoji}</span>
            <span style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>{label}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => updateDuration(p, Math.floor(durations[p]/60) - 1)}
              style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text2)', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>−</button>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', minWidth: 34, textAlign: 'center' }}>{Math.floor(durations[p]/60)}m</span>
            <button onClick={() => updateDuration(p, Math.floor(durations[p]/60) + 1)}
              style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text2)', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>+</button>
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: 'var(--bg)', borderRadius: 10, marginTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span style={{ fontSize: 16 }}>🔔</span>
          <span style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>Sound alerts</span>
        </div>
        <div onClick={() => setSound(p => !p)} style={{ width: 42, height: 24, borderRadius: 12, cursor: 'pointer', background: sound ? 'var(--accent)' : 'var(--border)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
          <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: sound ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.25)' }} />
        </div>
      </div>
    </div>
  );
}

// ── Main timer view ───────────────────────────────────────────────────────
function Timer({ ringSize = 164 }) {
  const { phase, running, round, sessionDone, display, pct, start, pause, reset, skip, switchPhase } = usePomodoro();
  const color    = C[phase];
  const roundPos = (round - 1) % 4;

  return (
    <div style={{ padding: '4px 18px 22px' }}>
      {/* Phase tabs */}
      <div style={{ display: 'flex', gap: 3, marginBottom: 18, background: 'var(--bg)', borderRadius: 10, padding: 4 }}>
        {Object.entries(PHASE_LABELS).map(([p, lbl]) => (
          <button key={p} onClick={() => switchPhase(p)} style={{
            flex: 1, padding: '7px 2px', borderRadius: 7, border: 'none',
            fontSize: 11, fontWeight: phase === p ? 700 : 500,
            cursor: 'pointer', fontFamily: 'inherit',
            background: phase === p ? 'var(--surface)' : 'transparent',
            color: phase === p ? color : 'var(--text3)',
            transition: 'all 0.15s',
            boxShadow: phase === p ? '0 1px 6px rgba(0,0,0,0.1)' : 'none',
            minHeight: 32, whiteSpace: 'nowrap',
          }}>{lbl}</button>
        ))}
      </div>

      {/* Ring */}
      <div style={{ position: 'relative', width: ringSize, height: ringSize, margin: '0 auto 12px' }}>
        <Arc pct={pct} size={ringSize} color={color} running={running} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span style={{ fontSize: 30, lineHeight: 1 }}>{EMOJI[phase]}</span>
          <div style={{ fontSize: 34, fontWeight: 800, color: 'var(--text)', letterSpacing: '-2px', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            {display}
          </div>
          <div style={{ display: 'flex', gap: 5 }}>
            {[0,1,2,3].map(i => (
              <div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: i < roundPos ? color : 'var(--border)', transition: 'background 0.3s' }} />
            ))}
          </div>
        </div>
      </div>

      {/* Session count */}
      <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--text3)', marginBottom: 16 }}>
        {sessionDone} session{sessionDone !== 1 ? 's' : ''} completed today
      </p>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center' }}>
        <button onClick={reset} title="Reset" style={{ width: 44, height: 44, borderRadius: '50%', border: '1.5px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--text2)'; e.currentTarget.style.color = 'var(--text)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text3)'; }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
        </button>

        <button onClick={running ? pause : start} style={{ width: 64, height: 64, borderRadius: '50%', border: 'none', background: color, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', boxShadow: `0 6px 24px ${color}55` }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.06)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
          {running
            ? <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16" rx="1.5"/><rect x="14" y="4" width="4" height="16" rx="1.5"/></svg>
            : <svg width="22" height="22" viewBox="0 0 24 24" fill="white" style={{ marginLeft: 3 }}><polygon points="5 3 19 12 5 21 5 3"/></svg>
          }
        </button>

        <button onClick={skip} title="Skip phase" style={{ width: 44, height: 44, borderRadius: '50%', border: '1.5px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--text2)'; e.currentTarget.style.color = 'var(--text)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text3)'; }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polygon points="5 4 15 12 5 20 5 4"/><line x1="19" y1="5" x2="19" y2="19"/></svg>
        </button>
      </div>
    </div>
  );
}

// ── The one clean TimerBody — single header, no duplication ───────────────
export function TimerBody({ onClose, showHandle = false, ringSize = 164 }) {
  const [showSettings, setShowSettings] = useState(false);
  const { phase, running, display } = usePomodoro();
  const color = C[phase];

  return (
    <div style={{ background: 'var(--surface)', borderRadius: 'inherit', overflow: 'hidden' }}>

      {/* Mobile drag handle — only shown when requested (bottom sheet) */}
      {showHandle && (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 2, background: 'var(--surface)' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)' }} />
        </div>
      )}

      {/* ── Single clean header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: showHandle ? '10px 16px 12px' : '14px 16px',
        borderBottom: '1px solid var(--border)',
      }}>
        {/* Left: title + live time when running */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>
            {EMOJI[phase]}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2 }}>Pomodoro</div>
            {running
              ? <div style={{ fontSize: 11, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{display} · {showSettings ? 'Settings' : PHASE_LABELS[phase]}</div>
              : <div style={{ fontSize: 11, color: 'var(--text3)' }}>{showSettings ? 'Settings' : PHASE_LABELS[phase]}</div>
            }
          </div>
        </div>

        {/* Right: settings toggle + close */}
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => setShowSettings(p => !p)}
            title={showSettings ? 'Back to timer' : 'Settings'}
            style={{
              width: 32, height: 32, borderRadius: 8, border: 'none',
              background: showSettings ? 'var(--accent-light)' : 'transparent',
              cursor: 'pointer', color: showSettings ? 'var(--accent)' : 'var(--text3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { if (!showSettings) { e.currentTarget.style.background = 'var(--hover)'; e.currentTarget.style.color = 'var(--text)'; }}}
            onMouseLeave={e => { if (!showSettings) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text3)'; }}}
          >
            {showSettings
              ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
            }
          </button>

          <button
            onClick={onClose}
            title="Close"
            style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, lineHeight: 1, transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover)'; e.currentTarget.style.color = 'var(--text)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text3)'; }}
          >×</button>
        </div>
      </div>

      {/* Body */}
      <div style={{ paddingTop: 16 }}>
        {showSettings ? <Settings /> : <Timer ringSize={ringSize} />}
      </div>
    </div>
  );
}

// ── Global modal at AppLayout level ──────────────────────────────────────
export function PomodoroModal() {
  const { open, setOpen } = usePomodoro();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  useEffect(() => {
    if (!open) return;
    const fn = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [open, setOpen]);

  if (!open) return null;

  if (isMobile) {
    return (
      <>
        <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }} />
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 501, borderRadius: '20px 20px 0 0', overflow: 'hidden', boxShadow: '0 -8px 40px rgba(0,0,0,0.2)', animation: 'pomodoroSlideUp 0.3s cubic-bezier(0.32,0.72,0,1)' }}>
          <TimerBody onClose={() => setOpen(false)} showHandle ringSize={180} />
        </div>
        <style>{`@keyframes pomodoroSlideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
      </>
    );
  }

  return (
    <>
      <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', zIndex: 501, transform: 'translate(-50%, -50%)', width: 330, borderRadius: 18, border: '1px solid var(--border)', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', animation: 'pomodoroFadeIn 0.2s ease' }}>
        <TimerBody onClose={() => setOpen(false)} ringSize={172} />
      </div>
      <style>{`@keyframes pomodoroFadeIn{from{opacity:0;transform:translate(-50%,-48%)}to{opacity:1;transform:translate(-50%,-50%)}}`}</style>
    </>
  );
}

// ── Sidebar trigger ───────────────────────────────────────────────────────
export function PomodoroTrigger() {
  const { running, display, phase, open, setOpen } = usePomodoro();
  const color = C[phase];

  return (
    <>
      <button onClick={() => setOpen(p => !p)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 9,
        padding: '8px 10px', borderRadius: 8, marginBottom: 0,
        background: open ? 'var(--accent-light)' : 'transparent',
        border: 'none', cursor: 'pointer',
        color: open ? 'var(--accent)' : 'var(--text2)',
        fontSize: 13.5, fontWeight: open ? 600 : 400,
        fontFamily: 'inherit', transition: 'all 0.12s', minHeight: 38,
      }}
        onMouseEnter={e => { if (!open) { e.currentTarget.style.background = 'var(--hover)'; }}}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.background = 'transparent'; }}}
      >
        <span style={{ display: 'flex', position: 'relative', flexShrink: 0, opacity: open ? 1 : 0.6 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          {running && <span style={{ position: 'absolute', top: -2, right: -2, width: 6, height: 6, borderRadius: '50%', background: color, animation: 'pomoPulse 1.5s ease-in-out infinite' }} />}
        </span>
        <span style={{ flex: 1, textAlign: 'left' }}>Pomodoro</span>
        {running && <span style={{ fontSize: 11, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{display}</span>}
      </button>
      <style>{`@keyframes pomoPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.5;transform:scale(1.5)}}`}</style>
    </>
  );
}